import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { ADDITIONAL_PERSONALIZATION_SUBSTEP_ID } from "src/pages/configurator/Configurator.utils";
import { FlowStep } from "src/pages/configurator/stepTemplates/FlowSteps";
import { Css, Properties } from "~generated/css";
import { useTestIds } from "~utils";
import { UserWarning } from "./UserWarning";

type MissingInsideMapsRenderingWarningProps = {
  isSelected: boolean;
  missingInsideMapsRendering: boolean;
  /** Optional style overrides forwarded to UserWarning (defaults to no top margin for card layouts). */
  xss?: Properties;
};

/**
 * Shows a warning under option cards only when the option is selected, flagged as missing an
 * Inside Maps rendering, and the current Interior personalization step renders the InsideMaps
 * iframe (excludes Additional Personalization, Options, Structural, ADU, etc.).
 */
export function MissingInsideMapsRenderingWarning(props: MissingInsideMapsRenderingWarningProps) {
  const { isSelected, missingInsideMapsRendering, xss } = props;
  const tid = useTestIds(props, "missing-rendering-warning");
  const showOnStep = useIsInsideMapsPersonalizationStep();

  if (!isSelected || !missingInsideMapsRendering || !showOnStep) return null;

  return (
    <div css={Css.w100.$} {...tid}>
      <UserWarning title="This selection is not visible in our preview image." xss={{ ...Css.mt0.$, ...xss }} />
    </div>
  );
}

/**
 * We only care about showing these warnings on steps where we actually show renderings.
 * If a user mistakenly marks an option that we will never render, we don't want to show a warning.
 */
function useIsInsideMapsPersonalizationStep(): boolean {
  const { currentFlowStep, currentSubStep, checkoutConfig } = useConfiguratorContext();
  if (currentFlowStep !== FlowStep.Interior) return false;
  const subStep = checkoutConfig?.interiorSubSteps.find((s) => s.stepNumber === currentSubStep);
  // Additional Personalization uses a static image instead of the InsideMaps iframe
  return !!subStep && subStep.id !== ADDITIONAL_PERSONALIZATION_SUBSTEP_ID;
}
